/**
 * Session status values.
 */
export const SessionStatus = {
  Active: 0,
  Paused: 1,
  Completed: 2,
  Cancelled: 3,
  Expired: 4,
  Error: 5,
} as const;

export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

/**
 * Message role values.
 */
export const MessageRole = {
  System: 0,
  User: 1,
  Assistant: 2,
  Tool: 3,
} as const;

export type MessageRole = (typeof MessageRole)[keyof typeof MessageRole];

/**
 * Agent session data transfer object.
 */
export interface AgentSessionDto {
  id: string;
  agentDefinitionId: string;
  agentName: string;
  sessionName: string;
  status: SessionStatus;
  messageCount: number;
  totalTokensUsed: number;
  totalCost: number;
  createdAt: string;
  lastActivityAt?: string;
  tenantId: string;
  projectId?: string;
  initialParametersJson?: string;
  hasContextSummaries?: boolean;
  contextSummaryCount?: number;
}

/**
 * Agent message data transfer object.
 */
export interface AgentMessageDto {
  id: string;
  sessionId: string;
  role: MessageRole;
  content: string;
  sequenceNumber: number;
  toolCallsJson?: string;
  toolResultsJson?: string;
  mediaType?: string | null;
  fileId?: string | null;
  mediaMetadataJson?: string | null;
  processedContentJson?: string | null;
  tokensUsed?: number;
  cost?: number;
  processedAt?: string;
  createdAt: string;
}

/**
 * Request model for creating a session.
 */
export interface CreateSessionRequest {
  agentDefinitionId: string;
  /**
   * Optional friendly name. When omitted, the backend defaults the
   * session name to the newly generated session identifier.
   */
  sessionName?: string;
  parameters?: Record<string, any>;
  initialMessage?: string;
}

/**
 * Request model for updating a session.
 */
export interface UpdateSessionRequest {
  sessionName?: string;
}

/**
 * Request model for sending a message.
 */
export interface SendMessageRequest {
  content: string;
  fileId?: string;
  mediaType?: string;
  mediaMetadataJson?: string;
  language?: string;
}

/**
 * Filters for session queries.
 */
export interface SessionFilters {
  pageNumber?: number;
  pageSize?: number;
  agentDefinitionId?: string;
  status?: SessionStatus;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
}
